import React, { useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import Sidebar from './components/Sidebar';
import WizardForm from './components/WizardForm';
import SuccessScreen from './components/SuccessScreen';
import ErrorPage from './components/ErrorPage';
import { useBranchesQuery, useTenantConfigQuery } from './api/queries';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// ─────────────────────────────────────────────
// AppContent — reads queries and handles errors
// ─────────────────────────────────────────────
function AppContent() {
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult]       = useState(null);

  const {
    data: tenantConfig,
    isLoading: loadingTenant,
    error: tenantError,
  } = useTenantConfigQuery();
   const {
    data: branches = [],
    isLoading: loadingBranches,
    error: branchesError,
  } = useBranchesQuery();

  
   const primaryColor = tenantConfig?.color || '#e9e6e6';

  // ── Show full page error if tenant or branches fail ──────
  if (tenantError) {
    return <ErrorPage message={tenantError?.message || 'TENANT_ERROR'} />;
  }

  if (branchesError) {
    return <ErrorPage message={branchesError?.message || 'BRANCHES_FETCH_FAILED'} />;
  }

  // ── Loading state — wait for tenant config at minimum ────
  if (loadingTenant) {
    return (
      <div style={loaderStyles.wrapper}>
        <div style={loaderStyles.spinner} />
        <p style={loaderStyles.text}>Loading application...</p>
      </div>
    );
  }
  
  // ── Success screen after form submit ─────────────────────
  if (submitted) {
    return (
      <div className="app-container" style={{ '--primary-color': primaryColor }}>
        <Sidebar />
        <div className="content-right">
          <SuccessScreen
            result={result}
            onReset={() => { setSubmitted(false); setResult(null); }}
          />
        </div>
      </div>
    );
  }

  // ── Main form ─────────────────────────────────────────────
  return (
    <div className="app-container" style={{ '--primary-color': primaryColor }}>
      <Sidebar />
      <div className="content-right">
        <WizardForm
          branches={branches}
          loadingBranches={loadingBranches}
          onSuccess={(res) => { setResult(res); setSubmitted(true); }}
        />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// Root App — wraps with QueryClientProvider
// ─────────────────────────────────────────────
export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AppContent />
    </QueryClientProvider>
  );
}

// ─────────────────────────────────────────────
// Loader styles
// ─────────────────────────────────────────────
const loaderStyles = {
  wrapper: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f5f5f5',
    fontFamily: 'sans-serif',
    gap: '16px',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '4px solid #e5e7eb',
    borderTop: '4px solid #111827',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  text: {
    fontSize: '15px',
    color: '#6B7280',
  },
};